import { useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { PersonalForm, ContactForm, AddressForm, OfficeForm, PasswordForm, PrivacyForm, ProfilePictureForm, CVForm, StudyForm, SchoolForm, LanguageForm, DrivingLicenseForm, PlaceForm, JobAreaForm, WorkingTimeForm, ContractForm, DOQForm, IncomeAccounting } from "components/Form";
import { useAuth } from "providers/AuthProvider";
import { getEmptyEditDto, mergeValueToRef } from 'utils/FormUtils';
import axios from 'axios';
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import {
    PiAddressBookDuotone,
    PiBookOpenTextDuotone,
    PiBuildingOfficeDuotone,
    PiCalendarDuotone,
    PiCameraDuotone,
    PiCarDuotone,
    PiCheckCircleDuotone,
    PiFileTextDuotone,
    PiIdentificationCardDuotone,
    PiLockKeyDuotone,
    PiMagnifyingGlassDuotone,
    PiMapPinDuotone,
    PiShieldCheckDuotone,
    PiStudentDuotone,
    PiTranslateDuotone,
    PiUserCircleDuotone,
    PiWalletDuotone,
    PiXCircleDuotone,
} from 'react-icons/pi';
import PageHeading from 'elements/PageHeading';
import Pattern from 'elements/Pattern';
import FeedbackModal from 'components/FeedbackModal';

const PROFILE_FORM_STYLE = {
    variant: 'registration',
    color: 'text-neutral-900',
    backgroundColor: 'gradient-green',
    borderColor: 'border-green-500',
};

const PAGE_TITLES = {
    personal: 'Személyes adataim',
    picture: 'Profilképem',
    cv: 'Önéletrajzom',
    preferences: 'Munkakeresési szempontjaim',
    study: 'Tanulmányaim, képességeim',
    contract: 'Szerződéskötés és digitális ügyintézés',
    monthly_income_accounting: 'Havi jövedelem-elszámolásaim',
    yearly_income_accounting: 'Éves jövedelem-elszámolásaim',
};

export default function FormPage() {
    const { userData, refreshUserData  } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [mode, setMode] = useState(null);
    const refreshInProgress = useRef(false);

    useEffect(() => {
        console.log('Search params updated:', searchParams);
        const blocklistParam = searchParams.get('blockList');
        
        if (blocklistParam !== mode) {
            setMode(blocklistParam);
        }
    }, [searchParams, mode]);     

    useEffect(() => {
        console.log('User data updated:', userData);
        if (!userData && typeof refreshUserData === 'function' && !refreshInProgress.current) {
            refreshInProgress.current = true; 
            refreshUserData();
        }
    }, []);

    const handleValueChange = (newData) => {
        if (JSON.stringify(newData) !== JSON.stringify(userData)) {
            console.log('Updated Data:', newData);
        }
    };    

    return(
        <div className='relative flex min-h-screen w-full flex-col items-center overflow-hidden gradient-green px-site py-site'>
            <Pattern className="pointer-events-none opacity-55" />

            <div className='profile-form-theme relative z-10 flex w-full max-w-boxed flex-col gap-8'>
                <PageHeading
                    title={PAGE_TITLES[mode] || 'Adataim'}
                    subTitle="A módosítások mentése után az adataid automatikusan frissülnek a profilodban."
                    align="left"
                    mobileAlign="center"
                />

                {mode === 'personal' && userData && (
                    <div className="flex min-w-0 flex-col gap-8">
                        <PersonalEdit />
                        <ContactEdit />
                        <OfficeEdit />
                        <AddressEdit />
                        <PasswordEdit />
                        <PrivacyEdit />
                    </div>
                )}
                {mode === 'picture' && userData && (
                    <div className="flex flex-col gap-8">
                        <ProfilePictureEdit />
                    </div>
                )}
                {mode === 'cv' && userData && (
                    <div className="flex flex-col gap-8">
                        <CVEdit />
                    </div>
                )}
                {mode === 'preferences' && userData && (
                    <div className="flex flex-col gap-8">
                        <PreferencesEdit />
                    </div>
                )}
                {mode === 'study' && userData && (
                    <div className="flex min-w-0 flex-col gap-8">
                        <StudyEdit />
                        <SchoolEdit />
                        <LanguageEdit />
                        <DrivingLicenseEdit /> 
                    </div>
                )}
                {mode === 'contract' && userData && (
                    <div className="flex min-w-0 flex-col gap-8">
                        <ContractEdit />
                        <DOQEdit />
                    </div>
                )}
                {mode === 'monthly_income_accounting' && userData && (
                    <div className="flex flex-col gap-8">
                        <IncomeAccounting
                            {...PROFILE_FORM_STYLE}
                            mode="monthly"
                            title="Havi jövedelem-elszámolásaim"
                            lead="Egyszerre csak egy, adott hónapra vonatkozó jövedelem-elszámolásodat tudod letölteni! A letöltött fájlok minden esetben PDF formátumúak."
                            value={userData}
                            onValueChange={handleValueChange}
                            icon={<PiCalendarDuotone />}
                            error={null}
                        />
                    </div>
                )}
                {mode === 'yearly_income_accounting' && userData && (
                    <div className="flex flex-col gap-8">
                        <IncomeAccounting
                            {...PROFILE_FORM_STYLE}
                            mode="yearly"
                            title='Éves jövedelem-elszámolásaim' 
                            lead='Az alábbi évre vonatkozó jövedelemigazolásomat szeretném letölteni! Egyszerre csak egy adott évre vonatkozó jövedelemigazolásodat tudod letölteni! Az igazolásokat PDF formátumban tudod letölteni, elmenteni, vagy kinyomtatni'
                            value={userData}
                            onValueChange={handleValueChange}
                            icon={<PiWalletDuotone />}
                            error={null}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function EditFormBlock(props) {
    const { refreshUserData } = useAuth();
    const [progress, setProgress] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const checkValidation = (responseData) => {
        return props.arrayResponse ? true : responseData.successful === true;
    };

    const getErrorMessage = (properties) => {
        if (!properties || typeof properties !== 'object') {
            return 'A módosításokat most nem sikerült elmenteni. Kérjük, próbáld újra.';
        }

        const firstMessage = Object.values(properties)
            .flatMap((value) => Array.isArray(value) ? value : [value])
            .find((item) => item?.message)?.message;

        return firstMessage || 'A módosításokat most nem sikerült elmenteni. Kérjük, ellenőrizd a megadott adatokat.';
    };

    const completeSuccessfulSave = () => {
        props.onError?.(null);

        const showSuccess = () => {
            setProgress(false);
            setFeedback({
                tone: 'success',
                title: 'Sikeres módosítás',
                message: 'A módosításaidat elmentettük, az adataid frissültek a profilodban.',
            });
        };

        if (typeof refreshUserData === 'function') {
            refreshUserData(showSuccess);
        } else {
            showSuccess();
        }
    };

    const completeFailedSave = (properties) => {
        props.onError?.(properties || null);
        setProgress(false);
        setFeedback({
            tone: 'error',
            title: 'A mentés nem sikerült',
            message: getErrorMessage(properties),
        });
    };

    const save = async () => {
        setProgress(true);

        try {
            if (typeof props.onSave === "function") {
                const result = await props.onSave();

                const ok = result === true || result?.successful === true || (props.arrayResponse ? true : false);

                if (ok) {
                    completeSuccessfulSave();
                } else {
                    completeFailedSave(result?.properties);
                }
                return;
            }

            const url = `${global.backingsEndpoint}${props.method}`;

            const latestData = typeof props.getData === "function" ? props.getData() : props.data;

            const body = { ...(latestData || {}) };

            console.log("Request Body:", JSON.stringify(body));

            const response = await axios.post(url, body);
            if (response.data) {
                if (props.arrayResponse ? true : response.data.successful === true) {
                    completeSuccessfulSave();
                } else {
                    completeFailedSave(response.data.properties);
                }
            }
        } catch (error) {
            console.error("Modification error.", error);
            completeFailedSave(error?.response?.data?.properties);
        }
    };

    return (
        <div className={`w-full ${props.className || ""}`}>
            <div>{props.children}</div>

            {props.changed && (
                <div className="form-actions mt-4">
                    <button
                        type="button"
                        className="form-action-button form-action-secondary flex-1"
                        onClick={props.onCancel}
                        disabled={progress}
                    >
                        <PiXCircleDuotone className="size-5 shrink-0" />
                        <span>Mégsem</span>
                    </button>

                    <button
                        type="button"
                        className={`form-action-button flex-1 ${progress ? "bg-gray-400 text-white" : "form-action-primary"}`}
                        onClick={save}
                        disabled={progress}
                    >
                        <PiCheckCircleDuotone className={`size-5 shrink-0 ${progress ? 'animate-pulse' : ''}`} />
                        <span>{progress ? "Mentés..." : "Mentés"}</span>
                    </button>
                </div>
            )}

            <FeedbackModal
                open={Boolean(feedback)}
                onClose={() => setFeedback(null)}
                tone={feedback?.tone}
                title={feedback?.title}
                message={feedback?.message}
                actionLabel="Rendben"
            />
        </div>
    );
}

function ProfilePictureEdit(props) {
    const { userData, refreshUserData } = useAuth();

    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const [resetKey, setResetKey] = useState(0);

    const fileRef = useRef(null);
    const removePhotoRef = useRef(false);

    useEffect(() => {
        fileRef.current = null;
        removePhotoRef.current = false;
        setChanged(false);
        setError(null);
    }, [userData]);

    const handleValueChange = (payload) => {
        const file = payload?.value?.file ?? null;
        const removePhoto = payload?.value?.removePhoto ?? false;

        fileRef.current = file;
        removePhotoRef.current = removePhoto;

        setChanged(!!file || removePhoto);
    };

    const handleCancel = () => {
        fileRef.current = null;
        removePhotoRef.current = false;
        setChanged(false);
        setError(null);
        setResetKey(k => k + 1);
    };

    const handleSave = async () => {
        const file = fileRef.current;
        const removePhoto = removePhotoRef.current;

        if (!file && !removePhoto) {
            return { successful: false, properties: { photo: ["Nincs módosítás."] } };
        }

        if (removePhoto) {
            const storeRes = await axios.post(
                `${global.backingsEndpoint}employeeUserControl/profileImageModification`,
                { dto: { photo: null } }
            );

            const result = storeRes?.data || { successful: false };

            if (result.successful) {
                fileRef.current = null;
                removePhotoRef.current = false;
                setChanged(false);
                setError(null);
                setResetKey(k => k + 1);
                refreshUserData?.();
            }

            return result;
        }

        const form = new FormData();
        form.append("file", file);

        const uploadRes = await axios.post(
            `${global.backingsEndpoint}photoRepository/addFile2`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } }
        );

        const photoKey = uploadRes?.data;
        if (!photoKey) {
            return { successful: false, properties: { photo: ["Sikertelen kép feltöltés."] } };
        }

        const storeRes = await axios.post(
            `${global.backingsEndpoint}employeeUserControl/profileImageModification`,
            { dto: { photo: photoKey } }
        );

        const result = storeRes?.data || { successful: false };

        if (result.successful) {
            fileRef.current = null;
            setChanged(false);
            setError(null);
            setResetKey(k => k + 1);
        }

        return result;
    };

    return (
        <EditFormBlock {...props} changed={changed} onError={(e) => setError(e)} onCancel={handleCancel} onSave={handleSave}>
            <ProfilePictureForm {...PROFILE_FORM_STYLE} icon={<PiCameraDuotone />} key={resetKey} edit error={error} value={userData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

const genericHandleValueChange = (ref, valueData, onChanged) => {
    const old = JSON.stringify(ref.current);
    // console.log(old);
    mergeValueToRef(valueData, ref);
    // console.log(JSON.stringify(ref.current));
    if (old !== JSON.stringify(ref.current)) onChanged?.();
};

function PasswordEdit(props) {
    const { userData } = useAuth();

    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetTrigger, setResetTrigger] = useState(false);

    const data = useRef(getEmptyEditDto());
    delete data.current.altPNDtoList;
    delete data.current.langDtoList;
    delete data.current.wtDto;

    const handleCancel = () => {
        setResetTrigger(prev => !prev);
        setChanged(false);
    };

    return (
        <EditFormBlock {...props} data={data.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/passwordModification'>
            <PasswordForm {...PROFILE_FORM_STYLE} icon={<PiLockKeyDuotone />} edit error={error} value={userData} onValueChange={(value) => genericHandleValueChange(data, value, () => setChanged(true))} resetTrigger={resetTrigger} />
        </EditFormBlock>
    );
}

function PersonalEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const data = useRef(getEmptyEditDto());
    delete data.current.altPNDtoList;
    delete data.current.langDtoList;
    delete data.current.wtDto;

    const initialDtoRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        data.current = getEmptyEditDto();
        delete data.current.altPNDtoList;
        delete data.current.langDtoList;
        delete data.current.wtDto;

        data.current.dto = {
            birthDate: userData?.birthDate ?? null,
            firstName: userData?.firstName ?? "",
            lastName: userData?.lastName ?? "",
            gender: userData?.gender ?? "",
        };

        initialDtoRef.current = cloneDeep(data.current);
        setChanged(false);
        setError(null);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, data);
        setChanged(!isEqual(data.current, initialDtoRef.current));
    };

    const handleCancel = () => {
        setFormData({ ...userData });
        data.current = cloneDeep(initialDtoRef.current);
        setChanged(false);
        setError(null);
    };

    return (
        <EditFormBlock {...props} data={data.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/personalDataModification'>
            <PersonalForm {...PROFILE_FORM_STYLE} icon={<PiUserCircleDuotone />} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function buildContactDtoFromUser(userData) {
  return {
    email: userData?.email ?? "",
    phoneNumber: userData?.phoneNumber ?? null,
    serviceProviderNumber: userData?.serviceProviderNumber ?? null,
    countryCode: 1,
    contactPercent: false,
    contractingPercent: false,
    cvUploaded: false,
    doqTermsAccepted: false,
    drivingPercent: false,
    hasCar: false,
    languagePercent: false,
    photoPercent: false,
    schoolPercent: false,
    studiesPercent: false,
    updatedAddress: false,
    updatedBirthDate: false,
    updatedDOQId: false,
    updatedEmail: false,
    updatedGoldcardID: false,
    updatedStudentCardNumber: false,
    updatedTaxId: false,
    updatedTempAddress: false,
  };
}

function ContactEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    // use for reseting the original data when clicking cancel button
    const [resetKey, setResetKey] = useState(0);

    const dataRef = useRef(null);
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        const initial = getEmptyEditDto();
        delete initial.altPNDtoList;
        delete initial.langDtoList;
        delete initial.wtDto;

        initial.dto = buildContactDtoFromUser(userData);
        initial.altPNDtoList = {
            alternativePhoneNumbers: userData?.alternativePhoneNumberList ?? [],
        };

        dataRef.current = cloneDeep(initial);
        initialRef.current = cloneDeep(initial);

        setChanged(false);
        setError(null);

        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        dataRef.current = { ...(dataRef.current || {}), ...(payload || {}) };
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData({ ...userData });
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} getData={() => dataRef.current} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/contactDataModification'>
            <ContactForm {...PROFILE_FORM_STYLE} icon={<PiAddressBookDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function AddressEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData({
            ...userData,
            postCode: userData?.zipCode ?? userData?.postCode ?? "",
            tempPostCode: userData?.tempZipCode ?? userData?.tempPostCode ?? "",
        });

        const dto = {
            country: userData?.country ?? null,
            postCode: userData?.zipCode ?? userData?.postCode ?? "",
            settlement: userData?.settlement ?? null,
            roadName: userData?.roadName ?? "",
            typesOfRoad: userData?.typesOfRoad ?? null,
            houseNumber: userData?.houseNumber ?? "",
            building: userData?.building ?? "",
            stairway: userData?.stairway ?? "",
            floor: userData?.floor ?? "",
            door: userData?.door ?? "",

            tempCountry: userData?.tempCountry ?? null,
            tempPostCode: userData?.tempZipCode ?? userData?.tempPostCode ?? "",
            tempSettlement: userData?.tempSettlement ?? null,
            tempRoadName: userData?.tempRoadName ?? "",
            tempTypesOfRoad: userData?.tempTypesOfRoad ?? null,
            tempHouseNumber: userData?.tempHouseNumber ?? "",
            tempBuilding: userData?.tempBuilding ?? "",
            tempStairway: userData?.tempStairway ?? "",
            tempFloor: userData?.tempFloor ?? "",
            tempDoor: userData?.tempDoor ?? "",
        };

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;
        dataRef.current.dto = dto;

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData({ ...userData });
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/addressDataModification'>
            <AddressForm {...PROFILE_FORM_STYLE} icon={<PiMapPinDuotone />} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function OfficeEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;

        dataRef.current.dto = dataRef.current.dto || {};
        dataRef.current.dto.officeList = userData?.officeList?.length ? [userData.officeList[0]] : [];

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData(userData);
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/officeOptionModification'>
            <OfficeForm {...PROFILE_FORM_STYLE} icon={<PiBuildingOfficeDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function PrivacyEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;

        dataRef.current.dto = {
            privacyStatementAccepted: true,
            subscribeNewsletter: userData?.subscribeNewsletter ?? false,
        };

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        dataRef.current = cloneDeep(initialRef.current);
        setFormData(userData);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/privacyModification'>
            <PrivacyForm {...PROFILE_FORM_STYLE} icon={<PiShieldCheckDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function CVEdit(props) {
    const { userData } = useAuth();

    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const data = useRef(getEmptyEditDto());
    delete data.current.altPNDtoList;
    delete data.current.langDtoList;
    delete data.current.wtDto;

    const initialDtoRef = useRef(cloneDeep(data.current));

    useEffect(() => {
        data.current = getEmptyEditDto();
        delete data.current.altPNDtoList;
        delete data.current.langDtoList;
        delete data.current.wtDto;

        data.current.dto = data.current.dto || {};
        data.current.dto.cvAcceptTerms = userData?.cvAcceptTerms ?? false;

        const existingKey = userData?.cvFileKey ?? userData?.cvFile ?? null;

        data.current.dto.cvFile = existingKey;
        data.current.dto.cvUploaded = !!(userData?.cvExpirationDate || existingKey);
        data.current.dto.cvAcceptTerms = userData?.cvAcceptTerms ?? false;

        initialDtoRef.current = cloneDeep(data.current);
        setChanged(false);
        setError(null);
        setResetKey(k => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, data);
        setChanged(!isEqual(data.current, initialDtoRef.current));
    };

    const handleCancel = () => {
        data.current = cloneDeep(initialDtoRef.current);
        setChanged(false);
        setError(null);
        setResetKey(k => k + 1);
    };

    return (
        <EditFormBlock {...props} data={data.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/profileCvModification'>
            <CVForm {...PROFILE_FORM_STYLE} icon={<PiFileTextDuotone />} key={resetKey} edit error={error} value={userData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function PreferencesEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;

        dataRef.current.dto = {
            workCountyList: userData?.workCountyList ?? [],
            workSettlementList: userData?.workSettlementList ?? [],
            workJobAreaList: userData?.workJobAreaList ?? [],
        };

        dataRef.current.wtDto = {
            workingTime: userData?.workingTimeList ?? [],
        };

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (valueData) => {
        const isWorkingTime = valueData?.config?.dto === "workDtoList" && valueData?.value?.workingTime;

        const vd = isWorkingTime
            ? { ...valueData, config: { ...valueData.config, dto: "wtDto" } }
            : valueData;

        mergeValueToRef(vd, dataRef);
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };


    const handleCancel = () => {
        setFormData(userData);
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} group data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/workSettingsModification'>
            <div className='flex flex-col gap-4'>
                <PlaceForm {...PROFILE_FORM_STYLE} icon={<PiMapPinDuotone />} key={`place-${resetKey}`} edit group error={error} value={formData} onValueChange={handleValueChange} />
                <JobAreaForm {...PROFILE_FORM_STYLE} icon={<PiMagnifyingGlassDuotone />} key={`job-${resetKey}`} edit group error={error} value={formData} onValueChange={handleValueChange} />
                <WorkingTimeForm {...PROFILE_FORM_STYLE} icon={<PiCalendarDuotone />} key={`wt-${resetKey}`} edit group error={error} value={formData} onValueChange={handleValueChange} />
            </div>
        </EditFormBlock>
    );
}

function StudyEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;

        dataRef.current.dto = {
            collegeStudent: userData?.collegeStudent ?? null,
            activeSemester: userData?.activeSemester ?? null,
        };

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData({ ...userData });
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/studiesModification'>
            <StudyForm {...PROFILE_FORM_STYLE} icon={<PiStudentDuotone />} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function SchoolEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;

        dataRef.current.dto = {
            schoolName: userData?.schoolName ?? "",
            schoolSettlement: userData?.schoolSettlement ?? "",
            schoolSpecialization: userData?.schoolSpecialization ?? "",
            specialty: userData?.specialty ?? "",
        };

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData(userData);
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/schoolModification'>
            <SchoolForm {...PROFILE_FORM_STYLE} icon={<PiBookOpenTextDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function LanguageEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;
        delete dataRef.current.dto;

        dataRef.current.lsDto = {
            languageSkills: (userData?.languageSkills ?? []).map((s) => ({
                id: s.id,
                languageId: s.language,
                skillId: s.skill,
            })),
        };

        initialRef.current = cloneDeep(dataRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        if (!initialRef.current) return;
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData(userData);
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/languageModification'>
            <LanguageForm {...PROFILE_FORM_STYLE} icon={<PiTranslateDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function DrivingLicenseEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);
    const [resetKey, setResetKey] = useState(0);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
            delete dataRef.current.altPNDtoList;
            delete dataRef.current.langDtoList;
            delete dataRef.current.wtDto;
            delete dataRef.current.dto;

            dataRef.current.dlDto = {
                drivingLicence: userData?.drivingLicence ?? false,
                hasCar: userData?.hasCar ?? false,
                drivingLicenceTypeList: userData?.drivingLicenceTypeList ?? [],
            };
        initialRef.current = cloneDeep(dataRef.current);

        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        if (!initialRef.current) return;
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData(userData);
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/drivingLicenseModification'>
            <DrivingLicenseForm {...PROFILE_FORM_STYLE} icon={<PiCarDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function ContractEdit(props) {
    const { userData } = useAuth();

    const [formData, setFormData] = useState(userData);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const dataRef = useRef(getEmptyEditDto());
    const initialRef = useRef(null);
    const [resetKey, setResetKey] = useState(0);

    useEffect(() => {
        setFormData(userData);

        dataRef.current = getEmptyEditDto();
        delete dataRef.current.altPNDtoList;
        delete dataRef.current.langDtoList;
        delete dataRef.current.wtDto;

        dataRef.current.dto = {
            studentCardNumber: userData?.studentCardNumber ?? "",
            bankAccountNumber: userData?.bankAccountNumber ?? "",

            name: userData?.name ?? "",
            birthDate: userData?.birthDate ?? null,
            birthPlace: userData?.birthPlace ?? "",
            nationality: userData?.nationality ?? null,
            citizenshipList: userData?.citizenshipList ?? [],
            taxResidence: userData?.taxResidence ?? null,
            bornTitle: userData?.bornTitle ?? "",
            bornLastName: userData?.bornLastName ?? "",
            bornFirstName: userData?.bornFirstName ?? "",
            parentTitle: userData?.parentTitle ?? "",
            parentLastName: userData?.parentLastName ?? "",
            parentFirstName: userData?.parentFirstName ?? "",
            taxId: userData?.taxId ?? "",
            socialSecurityNumber: userData?.socialSecurityNumber ?? "",
            idCardNumber: userData?.idCardNumber ?? "",

            schoolName: userData?.schoolName ?? "",
            schoolSettlement: userData?.schoolSettlement ?? null,
            schoolSpecialization: userData?.schoolSpecialization ?? null,
            specialty: userData?.specialty ?? "",
            
            country: userData?.country ?? null,
            postCode: userData?.postCode ?? "",
            settlement: userData?.settlement ?? null,
            roadName: userData?.roadName ?? "",
            typesOfRoad: userData?.typesOfRoad ?? null,
            houseNumber: userData?.houseNumber ?? "",
            building: userData?.building ?? "",
            stairway: userData?.stairway ?? "",
            floor: userData?.floor ?? "",
            door: userData?.door ?? "",

            tempCountry: userData?.tempCountry ?? null,
            tempPostCode: userData?.tempPostCode ?? "",
            tempSettlement: userData?.tempSettlement ?? null,
            tempRoadName: userData?.tempRoadName ?? "",
            tempTypesOfRoad: userData?.tempTypesOfRoad ?? null,
            tempHouseNumber: userData?.tempHouseNumber ?? "",
            tempBuilding: userData?.tempBuilding ?? "",
            tempStairway: userData?.tempStairway ?? "",
            tempFloor: userData?.tempFloor ?? "",
            tempDoor: userData?.tempDoor ?? "",
        };

        initialRef.current = cloneDeep(dataRef.current);

        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    }, [userData]);

    const handleValueChange = (payload) => {
        mergeValueToRef(payload, dataRef);
        if (!initialRef.current) return;
        setChanged(!isEqual(dataRef.current, initialRef.current));
    };

    const handleCancel = () => {
        setFormData(userData);
        dataRef.current = cloneDeep(initialRef.current);
        setChanged(false);
        setError(null);
        setResetKey((k) => k + 1);
    };

    return (
        <EditFormBlock {...props} data={dataRef.current} changed={changed} onError={(error) => setError(error)} onCancel={handleCancel} method='employeeUserControl/contractingDataModification'>
            <ContractForm {...PROFILE_FORM_STYLE} icon={<PiIdentificationCardDuotone />} key={resetKey} edit error={error} value={formData} onValueChange={handleValueChange} />
        </EditFormBlock>
    );
}

function DOQEdit(props) {
    const { userData } = useAuth();

    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const data = useRef(getEmptyEditDto());
    delete data.current.altPNDtoList;
    delete data.current.langDtoList;
    delete data.current.wtDto;

    return (
        <DOQForm {...PROFILE_FORM_STYLE} {...props} icon={<PiShieldCheckDuotone />} edit error={error} value={userData} onValueChange={(value) => genericHandleValueChange(data, value, () => setChanged(true))} />
    );
}
